import BigNumber from 'bignumber.js';
import moment from 'moment';

import type DateRange from '../../lib/dateRange';
import { PeriodSizes } from '../../lib/constants';
import { type Measure, type MetricSeriesResult, type MeasureErrors } from '../../types';
import { OrderingSpecifier } from 'common/types/soql/api';
import { FeatureFlags } from '@socrata/platform/feature-flags';

import { joinWhereClauses } from '../helpers';
import { setupBatchCalculation, formatPeriodKey, formatFiscalYearKey } from './core';
import { type SoqlDataProvider } from 'common/visualizations/dataProviders';

/**
 * Batch calculation for recent value measures across multiple reporting periods.
 * Reduces query count from N to 1 query for N reporting periods.
 *
 * Recent calculation finds the most recent (latest) value within each reporting period
 * based on the date column, which is different from aggregation functions like sum/count.
 */

// Batch query column aliases
const BATCH_PERIOD_ALIAS = '__batch_period_alias__';
const BATCH_DATE_ALIAS = '__batch_date_alias__';
const BATCH_VALUE_ALIAS = '__batch_value_alias__';

/**
 * Raw query result row interface for batch recent calculations
 */
interface RecentQueryResultRow {
  [key: string]: string | number | null;
  [BATCH_PERIOD_ALIAS]: string;
  [BATCH_DATE_ALIAS]: string;
  [BATCH_VALUE_ALIAS]: string | number | null;
  __row_rank__: string;
}

/**
 * Batched result for recent calculations
 */
interface BatchedRecentResult {
  period: string;
  date: string;
  value: string | null;
}

/**
 * Builds a date range filter clause for SQL queries
 */
function buildDateRangeFilter(dateColumn: string, startDate: moment.Moment, endDate: moment.Moment): string {
  const startDateStr = startDate.format('YYYY-MM-DDTHH:mm:ss.SSS');
  const endDateStr = endDate.format('YYYY-MM-DDTHH:mm:ss.SSS');
  return `${dateColumn} >= '${startDateStr}' AND ${dateColumn} < '${endDateStr}'`;
}

/**
 * Builds a SoQL query with window function to get most recent row per period
 */
function buildQueryWithConditionalOrdering(
  selectClause: string,
  whereClause: string,
  dateColumn: string,
  reportingPeriods: DateRange[],
): string {
  // Safety check to prevent invalid SQL generation
  if (!selectClause?.trim() || !whereClause?.trim() || !dateColumn?.trim() || !reportingPeriods?.length) {
    throw new Error('Invalid query parameters: all clauses and reportingPeriods must be non-empty');
  }

  // Use window function to rank rows by date within each period
  const windowFunction = FeatureFlags.valueOrDefault('soda3_viz_2', false)
    ? `row_number() over (partition by ${BATCH_PERIOD_ALIAS} order by ${dateColumn} desc)`
    : `row_number() over (partition by ${BATCH_PERIOD_ALIAS} order by ${dateColumn} desc, :id DESC)`;

  // Add window function to select clause to rank rows
  const enhancedSelectClause = `${selectClause}, ${windowFunction} as __row_rank__`;

  return `
    select
      ${enhancedSelectClause}
    where ${whereClause}
    order by ${BATCH_PERIOD_ALIAS}, ${dateColumn} DESC
    limit ${reportingPeriods.length}
  `.trim();
}

/**
 * Builds a SoQL query for recent values using CASE statements and window functions
 */
function buildRecentBatchQuery(
  valueColumn: string,
  dateColumn: string,
  whereClauses: string[],
  reportingPeriods: DateRange[],
  periodSize: PeriodSizes,
  fiscalYearStartMonth?: number,
): string {
  // Build CASE statements for each period
  const caseStatements: string[] = [];
  const overallStartDate = moment.min(reportingPeriods.map((p) => p.start));
  const overallEndDate = moment.max(reportingPeriods.map((p) => p.end));

  if (periodSize === PeriodSizes.YEAR) {
    // Handle fiscal years with CASE statement
    const fiscalYearRanges = new Map<string, { start: moment.Moment; end: moment.Moment }>();

    reportingPeriods.forEach((period) => {
      const fyStartMonth = fiscalYearStartMonth ?? 0;
      const fiscalYearKey = formatFiscalYearKey(period.start, fyStartMonth);

      if (!fiscalYearRanges.has(fiscalYearKey)) {
        const periodDate = period.start;
        let fiscalYearStart: moment.Moment;

        if (periodDate.month() >= fyStartMonth) {
          fiscalYearStart = moment([periodDate.year(), fyStartMonth, 1]);
        } else {
          fiscalYearStart = moment([periodDate.year() - 1, fyStartMonth, 1]);
        }

        const fiscalYearEnd = fiscalYearStart.clone().add(1, 'year');
        fiscalYearRanges.set(fiscalYearKey, { start: fiscalYearStart, end: fiscalYearEnd });
      }
    });

    fiscalYearRanges.forEach((dateRange, fiscalYearKey) => {
      const startDate = dateRange.start.format('YYYY-MM-DDTHH:mm:ss.SSS');
      const endDate = dateRange.end.format('YYYY-MM-DDTHH:mm:ss.SSS');

      caseStatements.push(
        `WHEN ${dateColumn} >= '${startDate}' AND ${dateColumn} < '${endDate}' THEN '${fiscalYearKey}'`,
      );
    });
  } else if (periodSize === PeriodSizes.WEEK || periodSize === PeriodSizes.QUARTER) {
    // Handle weekly and quarterly periods with CASE statement
    reportingPeriods.forEach((period) => {
      const periodKey = formatPeriodKey(period.start, periodSize);
      const startDate = period.start.format('YYYY-MM-DDTHH:mm:ss.SSS');
      const endDate = period.end.format('YYYY-MM-DDTHH:mm:ss.SSS');

      caseStatements.push(
        `WHEN ${dateColumn} >= '${startDate}' AND ${dateColumn} < '${endDate}' THEN '${periodKey}'`,
      );
    });
  } else if (periodSize === PeriodSizes.DAY) {
    // Use date truncation for daily periods
    return buildDailyRecentQuery(
      valueColumn,
      dateColumn,
      whereClauses,
      overallStartDate,
      overallEndDate,
      reportingPeriods,
    );
  } else if (periodSize === PeriodSizes.MONTH) {
    // Use date truncation for monthly periods
    return buildMonthlyRecentQuery(
      valueColumn,
      dateColumn,
      whereClauses,
      overallStartDate,
      overallEndDate,
      reportingPeriods,
    );
  }

  const caseExpression = `CASE\n      ${caseStatements.join('\n      ')}\n    END`;

  // Add overall date range filter to optimize query
  const overallDateFilter = buildDateRangeFilter(dateColumn, overallStartDate, overallEndDate);
  const allWhereClauses = [...whereClauses, overallDateFilter, `${valueColumn} is not null`];

  // Use window function to get the most recent value per period
  const whereClause = joinWhereClauses(allWhereClauses);
  const selectClause = `${caseExpression} as ${BATCH_PERIOD_ALIAS},
      ${dateColumn} as ${BATCH_DATE_ALIAS},
      ${valueColumn} as ${BATCH_VALUE_ALIAS}`;

  return buildQueryWithConditionalOrdering(selectClause, whereClause, dateColumn, reportingPeriods);
}

/**
 * Builds a daily recent query using date truncation
 */
function buildDailyRecentQuery(
  valueColumn: string,
  dateColumn: string,
  whereClauses: string[],
  startDate: moment.Moment,
  endDate: moment.Moment,
  reportingPeriods: DateRange[],
): string {
  const batchDateFilter = buildDateRangeFilter(dateColumn, startDate, endDate);
  const allWhereClauses = [...whereClauses, batchDateFilter, `${valueColumn} is not null`];
  const whereClause = joinWhereClauses(allWhereClauses);
  const selectClause = `date_trunc_ymd(${dateColumn}) as ${BATCH_PERIOD_ALIAS},
      ${dateColumn} as ${BATCH_DATE_ALIAS},
      ${valueColumn} as ${BATCH_VALUE_ALIAS}`;

  return buildQueryWithConditionalOrdering(selectClause, whereClause, dateColumn, reportingPeriods);
}

/**
 * Builds a monthly recent query using date truncation
 */
function buildMonthlyRecentQuery(
  valueColumn: string,
  dateColumn: string,
  whereClauses: string[],
  startDate: moment.Moment,
  endDate: moment.Moment,
  reportingPeriods: DateRange[],
): string {
  const batchDateFilter = buildDateRangeFilter(dateColumn, startDate, endDate);
  const allWhereClauses = [...whereClauses, batchDateFilter, `${valueColumn} is not null`];
  const whereClause = joinWhereClauses(allWhereClauses);
  const selectClause = `date_trunc_ym(${dateColumn}) as ${BATCH_PERIOD_ALIAS},
      ${dateColumn} as ${BATCH_DATE_ALIAS},
      ${valueColumn} as ${BATCH_VALUE_ALIAS}`;

  return buildQueryWithConditionalOrdering(selectClause, whereClause, dateColumn, reportingPeriods);
}

/**
 * Executes a batched recent query for multiple reporting periods
 */
async function executeBatchedRecentQuery(
  dataProvider: SoqlDataProvider,
  valueColumn: string,
  dateColumn: string,
  whereClauses: string[],
  reportingPeriods: DateRange[],
  periodSize: PeriodSizes,
  fiscalYearStartMonth?: number,
): Promise<BatchedRecentResult[]> {
  if (reportingPeriods.length === 0) {
    return [];
  }

  const query = buildRecentBatchQuery(
    valueColumn,
    dateColumn,
    whereClauses,
    reportingPeriods,
    periodSize,
    fiscalYearStartMonth,
  );

  const orderingSpecifier = FeatureFlags.valueOrDefault('soda3_viz_2', false)
    ? { orderingSpecifier: OrderingSpecifier.Total }
    : undefined;

  const data = await dataProvider.invokeSoqlQuery(() => query, [], undefined, undefined, orderingSpecifier);

  // Filter to only the most recent row per period (rank 1) since SoQL doesn't support subqueries
  return data
    .filter((row: RecentQueryResultRow) => row.__row_rank__ === '1')
    .map((row: RecentQueryResultRow) => ({
      period: String(row[BATCH_PERIOD_ALIAS]),
      date: String(row[BATCH_DATE_ALIAS]),
      value: row[BATCH_VALUE_ALIAS] !== null ? String(row[BATCH_VALUE_ALIAS]) : null,
    }));
}

/**
 * Processes batch recent results and finds the most recent value per period
 */
function processBatchRecentResults(
  batchData: BatchedRecentResult[],
  adjustedReportingPeriods: DateRange[],
  periodSize: PeriodSizes,
  fiscalYearStartMonth: number | undefined,
  errors: MeasureErrors,
): Omit<MetricSeriesResult, 'date'>[] {
  // Group results by period and find the most recent value in each period
  const periodResults = new Map<string, { value: BigNumber; date: moment.Moment }>();

  batchData.forEach((row) => {
    if (row.value === null) {
      return;
    }

    let formattedKey: string;
    if (
      periodSize === PeriodSizes.WEEK ||
      periodSize === PeriodSizes.QUARTER ||
      periodSize === PeriodSizes.YEAR
    ) {
      formattedKey = row.period; // Already correctly formatted from CASE statement
    } else {
      const periodDate = moment(row.period);
      formattedKey = formatPeriodKey(periodDate, periodSize);
    }

    const rowDate = moment(row.date);
    const existingResult = periodResults.get(formattedKey);

    // Keep only the most recent (latest) value for each period
    if (!existingResult || rowDate.isAfter(existingResult.date)) {
      periodResults.set(formattedKey, {
        value: new BigNumber(row.value),
        date: rowDate,
      });
    }
  });

  // Map results back to reporting periods
  return adjustedReportingPeriods.map((period) => {
    let periodKey: string;
    if (periodSize === PeriodSizes.YEAR) {
      if (fiscalYearStartMonth === undefined) {
        return {
          errors: { ...errors, calculationNotConfigured: true },
          result: { value: null },
        };
      }
      periodKey = formatFiscalYearKey(period.start, fiscalYearStartMonth);
    } else {
      periodKey = formatPeriodKey(period.start, periodSize);
    }

    const data = periodResults.get(periodKey);

    if (!data || data.value.isNaN()) {
      return {
        errors: { ...errors, notEnoughData: true },
        result: { value: null },
      };
    }

    return {
      errors: { ...errors },
      result: { value: data.value },
    };
  });
}

/**
 * Calculates recent measures using batch queries for multiple reporting periods.
 * This is the main optimization that reduces N queries to 1 query.
 */
export const calculateBatchedRecentMeasures = async (
  measure: Measure | null,
  reportingPeriods: DateRange[],
): Promise<Omit<MetricSeriesResult, 'date'>[]> => {
  const setup = setupBatchCalculation(measure, reportingPeriods);

  if ('shouldReturn' in setup) {
    return reportingPeriods.map(() => ({ errors: { ...setup.errors }, result: { value: null } }));
  }

  const {
    errors,
    adjustedReportingPeriods,
    valueColumn,
    dateColumn,
    calculationWhereClause,
    periodSize,
    dataProvider,
    fiscalYearStartMonth,
  } = setup;

  try {
    // Execute single batched recent query
    const batchData = await executeBatchedRecentQuery(
      dataProvider,
      valueColumn,
      dateColumn,
      calculationWhereClause,
      adjustedReportingPeriods,
      periodSize,
      fiscalYearStartMonth,
    );

    return processBatchRecentResults(
      batchData,
      adjustedReportingPeriods,
      periodSize,
      fiscalYearStartMonth,
      errors,
    );
  } catch (error) {
    console.warn('Batch recent calculation failed:', error);
    throw error;
  }
};
